import type { Entry, UnresolvedLink } from 'contentful';
import type { Document } from '@contentful/rich-text-types';

import { toContentfulAsset, toMediaAsset } from 'components/shared/Media/toMediaAsset';
import { toCta } from 'components/shared/CTA/toCta';
import { toDocumentWithEmbeddedEntries } from 'libs/contentful/toDocumentWithEmbeddedEntries';
import { getColorwayName } from 'utils/formatters';
import { VIDEO_PLAYER_CONSTANTS } from 'components/VideoPlayer/constants';

import type {
  AspectRatio,
  ContentfulCollection,
  DocumentWithEmbeddedEntries,
  HyperlinkedMediaProps,
} from 'libs/contentful/types';
import type { VideoItemReference } from 'types/sharedComponents';
import type { YouTubeEmbedProps } from 'components/shared/YouTubeEmbed/types';
import type { AspectRatioSkeleton } from 'components/HeaderLogo/types';
import type { VideoItemSkeleton } from 'components/VideoPlayer/types';
import type { ResolvableVideoItemFields } from 'components/VideoPlayer/toVideoItem';

import {
  CardStyle,
  CardVariant,
  type CardSkeleton,
  type CardVariantProps,
  type HyperlinkedMediaSkeleton,
  type YouTubeEmbedSkeleton,
} from './types';

type ResolvedCard = Entry<CardSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
type ResolvedVideoItem = Entry<VideoItemSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
interface RuntimeVideoItem extends Omit<ResolvedVideoItem, 'fields'> {
  fields: ResolvableVideoItemFields;
}
type ResolvedVideo = RuntimeVideoItem | Entry<YouTubeEmbedSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
type RawVideo = ResolvedVideo | UnresolvedLink<'Entry'>;
type ResolvedHyperlinkedMedia = Entry<HyperlinkedMediaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
type RawHyperlinkedMedia = ResolvedHyperlinkedMedia | UnresolvedLink<'Entry'>;
type ResolvedAspectRatio = Entry<AspectRatioSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
type RawAspectRatio = ResolvedAspectRatio | UnresolvedLink<'Entry'>;
type RawCardAsset = NonNullable<ResolvedCard['fields']['desktopMedia']> | UnresolvedLink<'Asset'>;
interface RuntimeCardFields extends Omit<
  ResolvedCard['fields'],
  | 'cta'
  | 'iconImage'
  | 'desktopMedia'
  | 'mobileMedia'
  | 'desktopAspectRatio'
  | 'mobileAspectRatio'
  | 'hyperlinkedImages'
  | 'video'
> {
  cta?: NonNullable<ResolvedCard['fields']['cta']> | UnresolvedLink<'Entry'>;
  iconImage?: RawCardAsset;
  desktopMedia?: RawCardAsset;
  mobileMedia?: RawCardAsset;
  desktopAspectRatio?: RawAspectRatio;
  mobileAspectRatio?: RawAspectRatio;
  hyperlinkedImages?: Array<RawHyperlinkedMedia | undefined>;
  video?: RawVideo;
}
interface RuntimeCard extends Omit<ResolvedCard, 'fields'> {
  fields: RuntimeCardFields;
}

function toCardRichText(doc: Document | undefined, fieldName: string): DocumentWithEmbeddedEntries | undefined {
  return doc ? toDocumentWithEmbeddedEntries(doc, { parentTypename: 'Card', fieldName }) : undefined;
}

function toAspectRatio(entry: RawAspectRatio | undefined): AspectRatio | undefined {
  return entry && 'fields' in entry ? { value: entry.fields.value, name: entry.fields.name } : undefined;
}

function toHyperlinkedImages(
  images: Array<RawHyperlinkedMedia | undefined> | undefined,
): ContentfulCollection<HyperlinkedMediaProps> | undefined {
  if (!images) return undefined;

  const items = images.flatMap<HyperlinkedMediaProps>(hm => {
    if (!hm || !('fields' in hm) || !hm.fields.image) {
      return [];
    }

    const image = toMediaAsset(hm.fields.image);
    return image ? [{ link: hm.fields.link, image }] : [];
  });

  return { items };
}

function isYouTubeEmbed(video: ResolvedVideo): video is Entry<YouTubeEmbedSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> {
  return video.sys.contentType.sys.id === 'youTubeEmbed';
}

function toCardVideo(video: RawVideo | undefined): VideoItemReference | YouTubeEmbedProps | undefined {
  if (!video || !('fields' in video)) {
    return undefined;
  }

  if (isYouTubeEmbed(video)) {
    return { __typename: 'YouTubeEmbed', youTubeVideoUrl: video.fields.youTubeVideoUrl };
  }

  const previewImage = toContentfulAsset(video.fields.previewImage);
  if (!previewImage) {
    return undefined;
  }

  const aspectRatioEntry = video.fields.aspectRatio;
  // VideoPreview uses the portrait sentinel when the linked ratio is unavailable.
  const aspectRatio =
    (aspectRatioEntry && 'fields' in aspectRatioEntry ? aspectRatioEntry.fields.aspectRatio : undefined) ??
    VIDEO_PLAYER_CONSTANTS.PORTRAIT_ASPECT_RATIO;

  return {
    __typename: 'VideoItem',
    sys: { id: video.sys.id },
    video: toContentfulAsset(video.fields.video),
    videoUrl: video.fields.videoUrl,
    previewImage,
    overlayLabel: video.fields.overlayLabel,
    aspectRatio: { aspectRatio },
  };
}

function resolveCard(card: RuntimeCard): CardVariantProps {
  const base = {
    sys: { id: card.sys.id },
    __typename: 'Card',
    index: 0,
    total: 0,
    cardStyle: CardStyle.None,
    color: getColorwayName(card.fields.color),
    headline: toCardRichText(card.fields.headline, 'headline'),
    copy: toCardRichText(card.fields.copy, 'copy'),
  };

  switch (card.fields.variant) {
    case CardVariant.Icon:
      return {
        ...base,
        variant: CardVariant.Icon,
        iconImage: card.fields.iconImage && toMediaAsset(card.fields.iconImage),
        eyebrow: toCardRichText(card.fields.eyebrow, 'eyebrow'),
        showFiveStarEyebrow: card.fields.showFiveStarEyebrow,
        showChimeLogo: card.fields.showChimeLogo,
        hyperlinkedImages: toHyperlinkedImages(card.fields.hyperlinkedImages),
        hyperlinkedImagesLocation: card.fields.hyperlinkedImagesLocation,
        cta: toCta(card.fields.cta),
      };
    case CardVariant.Text:
      return { ...base, variant: CardVariant.Text };
    case CardVariant.Video:
      return { ...base, variant: CardVariant.Video, video: toCardVideo(card.fields.video) };
    // Unknown/absent variants default to Image, matching the legacy fallback.
    default:
      return {
        ...base,
        variant: CardVariant.Image,
        desktopMedia: card.fields.desktopMedia && toMediaAsset(card.fields.desktopMedia),
        mobileMedia: card.fields.mobileMedia && toMediaAsset(card.fields.mobileMedia),
        desktopAspectRatio: toAspectRatio(card.fields.desktopAspectRatio),
        mobileAspectRatio: toAspectRatio(card.fields.mobileAspectRatio),
        imageSize: card.fields.imageSize,
        eyebrow: toCardRichText(card.fields.eyebrow, 'eyebrow'),
        showFiveStarEyebrow: card.fields.showFiveStarEyebrow,
        showChimeLogo: card.fields.showChimeLogo,
        hyperlinkedImages: toHyperlinkedImages(card.fields.hyperlinkedImages),
        hyperlinkedImagesLocation: card.fields.hyperlinkedImagesLocation,
      };
  }
}

type RawCard = RuntimeCard | UnresolvedLink<'Entry'> | undefined;

export function resolveCards(rawCards: RawCard[] | undefined): CardVariantProps[] {
  return (rawCards ?? []).flatMap(card => (card != null && 'fields' in card ? [resolveCard(card)] : []));
}
